import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency, formatDate } from '../utils/helpers';
import { ArrowLeft, FileSpreadsheet, Plus, Trash2, Edit, Ban, Coins, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClientBillingPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [billingCycles, setBillingCycles] = useState([]);
  const [adhocCharges, setAdhocCharges] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCharge, setShowAddCharge] = useState(false);
  const [showEditCharge, setShowEditCharge] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [chargeForm, setChargeForm] = useState({
    description: '',
    amount: '',
    quantity: '1',
    charge_type: 'service',
    site_id: '',
    gst_applicable: true,
    gst_rate: '18',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [clientRes, cyclesRes, chargesRes, sitesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/clients/${clientId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/billing/cycles?client_id=${clientId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/adhoc-charges?client_id=${clientId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/sites?client_id=${clientId}`, { headers }),
      ]);
      
      setClient(clientRes.data);
      setBillingCycles(cyclesRes.data);
      setAdhocCharges(chargesRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Failed to fetch client billing data:', error);
      toast.error('Failed to load client billing data');
    } finally {
      setLoading(false);
    }
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site ? site.name : 'N/A';
  };

  const handleAddCharge = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/adhoc-charges`,
        {
          client_id: clientId,
          site_id: chargeForm.site_id || null,
          description: chargeForm.description,
          amount: parseFloat(chargeForm.amount),
          quantity: parseFloat(chargeForm.quantity),
          charge_type: chargeForm.charge_type,
          gst_applicable: chargeForm.gst_applicable,
          gst_rate: parseFloat(chargeForm.gst_rate),
          notes: chargeForm.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ad hoc charge created!');
      setShowAddCharge(false);
      resetChargeForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to create charge');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCharge = async (e) => {
    e.preventDefault();
    if (!editingCharge) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/adhoc-charges/${editingCharge.charge_id}`,
        {
          client_id: clientId,
          site_id: chargeForm.site_id || null,
          description: chargeForm.description,
          amount: parseFloat(chargeForm.amount),
          quantity: parseFloat(chargeForm.quantity),
          charge_type: chargeForm.charge_type,
          gst_applicable: chargeForm.gst_applicable,
          gst_rate: parseFloat(chargeForm.gst_rate),
          notes: chargeForm.notes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Charge updated!');
      setShowEditCharge(false);
      setEditingCharge(null);
      resetChargeForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to update charge');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelCharge = async (chargeId) => {
    if (!window.confirm('Cancel this charge?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/adhoc-charges/${chargeId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Charge cancelled');
      fetchData();
    } catch (error) {
      console.error('Failed to cancel charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel charge');
    }
  };

  const handleDeleteCharge = async (chargeId) => {
    if (!window.confirm('Delete this charge permanently?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/adhoc-charges/${chargeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Charge deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete charge');
    }
  };

  const openEditChargeModal = (charge) => {
    setEditingCharge(charge);
    setChargeForm({
      description: charge.description,
      amount: charge.amount.toString(),
      quantity: charge.quantity.toString(),
      charge_type: charge.charge_type,
      site_id: charge.site_id || '',
      gst_applicable: charge.gst_applicable,
      gst_rate: charge.gst_rate.toString(),
      notes: charge.notes || '',
    });
    setShowEditCharge(true);
  };

  const resetChargeForm = () => {
    setChargeForm({
      description: '', amount: '', quantity: '1', charge_type: 'service',
      site_id: '', gst_applicable: true, gst_rate: '18', notes: '',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'text-slate-600 bg-slate-100',
      approved: 'text-blue-600 bg-blue-100',
      locked: 'text-green-600 bg-green-100',
      pending: 'text-yellow-600 bg-yellow-100',
      invoiced: 'text-green-600 bg-green-100',
      paid: 'text-emerald-600 bg-emerald-100',
      cancelled: 'text-red-600 bg-red-100',
    };
    return colors[status] || 'text-slate-600 bg-slate-100';
  };

  const calculateChargeTotal = (charge) => {
    const subtotal = charge.amount * charge.quantity;
    if (charge.gst_applicable) {
      return subtotal + (subtotal * charge.gst_rate / 100);
    }
    return subtotal;
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64 p-8">
          <EmptyState icon={Receipt} title="Client not found" description="The requested client could not be found." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <Breadcrumbs
            items={[
              { label: 'Clients', to: '/clients' },
              { label: `${client.name} — Billing` },
            ]}
            onBack={() => navigate('/clients')}
          />
          
          <PageHeader
            title={`${client.name} - Billing`}
            description="View billing cycles and manage ad hoc charges"
          />

          <Tabs defaultValue="cycles" className="mt-6">
            <TabsList className="bg-white border border-slate-200 p-1">
              <TabsTrigger value="cycles" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-2" /> Billing Cycles
              </TabsTrigger>
              <TabsTrigger value="adhoc" className="text-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                <Coins className="w-4 h-4 mr-2" /> Ad Hoc Charges
              </TabsTrigger>
            </TabsList>

            {/* Billing Cycles Tab */}
            <TabsContent value="cycles" className="mt-4">
              {billingCycles.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No billing cycles found for this client.</p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th>Site</th>
                          <th>Billing Type</th>
                          <th>Actual kWh</th>
                          <th>Subtotal</th>
                          <th>GST</th>
                          <th>Total</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {billingCycles.map((cycle) => (
                          <tr key={cycle.cycle_id}>
                            <td className="font-medium">{cycle.year}-{String(cycle.month).padStart(2, '0')}</td>
                            <td>{getSiteName(cycle.site_id)}</td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs ${cycle.billing_type === 'fixed_rental' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {cycle.billing_type === 'fixed_rental' ? 'Fixed Rental' : 'Usage Based'}
                              </span>
                            </td>
                            <td>{(cycle.actual_discharge_kwh || 0).toFixed(2)}</td>
                            <td>{formatCurrency(cycle.subtotal || 0)}</td>
                            <td>{formatCurrency(cycle.gst_amount || 0)}</td>
                            <td className="font-semibold">{formatCurrency(cycle.total_amount || 0)}</td>
                            <td><span className={`px-2 py-1 rounded text-xs ${getStatusColor(cycle.status)}`}>{cycle.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Ad Hoc Charges Tab */}
            <TabsContent value="adhoc" className="mt-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Ad Hoc Charges</h3>
                <Button onClick={() => setShowAddCharge(true)} className="bg-slate-900" data-testid="add-adhoc-charge-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Charge
                </Button>
              </div>

              {adhocCharges.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <Coins className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No ad hoc charges for this client.</p>
                  <p className="text-sm text-slate-500 mt-1">Create one-time service charges, adjustments, or credits.</p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Site</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Qty</th>
                          <th>GST</th>
                          <th>Total</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adhocCharges.map((charge) => (
                          <tr key={charge.charge_id}>
                            <td className="font-medium">{charge.description}</td>
                            <td>{charge.site_id ? getSiteName(charge.site_id) : <span className="text-slate-400">Client-level</span>}</td>
                            <td>
                              <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-700 capitalize">{charge.charge_type}</span>
                            </td>
                            <td>{formatCurrency(charge.amount)}</td>
                            <td>{charge.quantity}</td>
                            <td>{charge.gst_applicable ? `${charge.gst_rate}%` : '-'}</td>
                            <td className="font-semibold">{formatCurrency(calculateChargeTotal(charge))}</td>
                            <td><span className={`px-2 py-1 rounded text-xs ${getStatusColor(charge.status)}`}>{charge.status}</span></td>
                            <td>
                              <div className="flex space-x-1">
                                {charge.status === 'pending' && (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => openEditChargeModal(charge)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleCancelCharge(charge.charge_id)}>
                                      <Ban className="w-4 h-4 text-orange-500" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteCharge(charge.charge_id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Ad Hoc Charge Dialog */}
      <Dialog open={showAddCharge} onOpenChange={setShowAddCharge}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Ad Hoc Charge</DialogTitle>
            <DialogDescription>Create a one-time charge for this client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddCharge} className="space-y-4">
            <div>
              <Label>Description *</Label>
              <Input value={chargeForm.description} onChange={(e) => setChargeForm({...chargeForm, description: e.target.value})} required placeholder="e.g., Site maintenance fee" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={chargeForm.amount} onChange={(e) => setChargeForm({...chargeForm, amount: e.target.value})} required />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" step="1" value={chargeForm.quantity} onChange={(e) => setChargeForm({...chargeForm, quantity: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Charge Type</Label>
                <Select value={chargeForm.charge_type} onValueChange={(v) => setChargeForm({...chargeForm, charge_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="penalty">Penalty</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site (Optional)</Label>
                <Select value={chargeForm.site_id || "none"} onValueChange={(v) => setChargeForm({...chargeForm, site_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Client-level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Client-level</SelectItem>
                    {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST Rate (%)</Label>
                <Input type="number" step="0.1" value={chargeForm.gst_rate} onChange={(e) => setChargeForm({...chargeForm, gst_rate: e.target.value})} />
              </div>
              <div className="flex items-center pt-6">
                <input type="checkbox" id="gst_applicable" checked={chargeForm.gst_applicable} onChange={(e) => setChargeForm({...chargeForm, gst_applicable: e.target.checked})} className="mr-2" />
                <Label htmlFor="gst_applicable" className="cursor-pointer">GST Applicable</Label>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={chargeForm.notes} onChange={(e) => setChargeForm({...chargeForm, notes: e.target.value})} rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddCharge(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Creating...' : 'Create Charge'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Ad Hoc Charge Dialog */}
      <Dialog open={showEditCharge} onOpenChange={setShowEditCharge}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Ad Hoc Charge</DialogTitle>
            <DialogDescription>Update this charge</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCharge} className="space-y-4">
            <div>
              <Label>Description *</Label>
              <Input value={chargeForm.description} onChange={(e) => setChargeForm({...chargeForm, description: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={chargeForm.amount} onChange={(e) => setChargeForm({...chargeForm, amount: e.target.value})} required />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" step="1" value={chargeForm.quantity} onChange={(e) => setChargeForm({...chargeForm, quantity: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Charge Type</Label>
                <Select value={chargeForm.charge_type} onValueChange={(v) => setChargeForm({...chargeForm, charge_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="penalty">Penalty</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site (Optional)</Label>
                <Select value={chargeForm.site_id || "none"} onValueChange={(v) => setChargeForm({...chargeForm, site_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Client-level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Client-level</SelectItem>
                    {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST Rate (%)</Label>
                <Input type="number" step="0.1" value={chargeForm.gst_rate} onChange={(e) => setChargeForm({...chargeForm, gst_rate: e.target.value})} />
              </div>
              <div className="flex items-center pt-6">
                <input type="checkbox" id="gst_applicable_edit" checked={chargeForm.gst_applicable} onChange={(e) => setChargeForm({...chargeForm, gst_applicable: e.target.checked})} className="mr-2" />
                <Label htmlFor="gst_applicable_edit" className="cursor-pointer">GST Applicable</Label>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={chargeForm.notes} onChange={(e) => setChargeForm({...chargeForm, notes: e.target.value})} rows={2} />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowEditCharge(false); setEditingCharge(null); }}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientBillingPage;
